import { fetchQuranJson } from './dataSource';

/**
 * Per-word audio and recitation timings come from Quran.com, which numbers the
 * words of a verse its own way. Our page layout (pages.json) numbers every
 * glyph on a line, and Quran.com's `audio_url` field is itself shifted by
 * pause marks — for 2:2 it names `_008.mp3` and `_009.mp3`, which do not exist
 * on the CDN, while the real files run `_001` to `_007`. So a word is matched
 * by its Arabic letters (diacritics and marks stripped), choosing the nearest
 * position when a word repeats, and the audio file is named by the word's
 * ordinal among real words.
 */

/** Harakat, Quranic annotation marks (U+06D6–U+06ED), superscript alef and tatweel. */
const MARKS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;

/**
 * The bare letters of an Arabic word, so two renderings of the same word
 * compare equal: alef variants fold to alef, alef maqsura and Farsi yeh to yeh
 * (our layout writes فِي, Quran.com فِى), Farsi kaf to kaf, whitespace dropped.
 */
export function stripArabic(text: string | null | undefined): string {
  return String(text ?? '')
    .normalize('NFD')
    .replace(MARKS, '')
    .replace(/[ٱآأإ]/g, 'ا')
    .replace(/[ىی]/g, 'ي')
    .replace(/ک/g, 'ك')
    .replace(/\s+/g, '');
}

/** True when the stripped text still holds an Arabic letter (not a verse number or a lone mark). */
export function isArabicWord(stripped: string): boolean {
  return /[ء-ي]/.test(stripped);
}

export interface QuranWord {
  /** Quran.com's position within the verse (1-based; what the timings refer to). */
  position: number;
  /** The word's bare letters (stripArabic). */
  text: string;
  /** char_type_name === 'word' — false for the verse-end ornament and other marks. */
  isWord: boolean;
  /** 1-based index among real words; 0 for non-words. Names the audio file. */
  ordinal: number;
  /** Quran.com's own audio_url field, kept only for diagnostics: it is unreliable. */
  audioUrlField: string;
}

interface QuranComWordsPayload {
  verse?: {
    words?: {
      position: number;
      char_type_name?: string;
      text_uthmani?: string;
      audio_url?: string | null;
    }[];
  };
}

export function quranWordsUrl(verseKey: string): string {
  return 'https://api.quran.com/api/v4/verses/by_key/' + verseKey + '?words=true&word_fields=audio_url,text_uthmani,char_type_name';
}

export function parseQuranWords(payload: QuranComWordsPayload): QuranWord[] {
  let ordinal = 0;
  return (payload.verse?.words ?? []).map((word) => {
    const isWord = word.char_type_name === 'word';
    if (isWord) ordinal++;
    return {
      position: word.position,
      text: stripArabic(word.text_uthmani),
      isWord,
      ordinal: isWord ? ordinal : 0,
      audioUrlField: word.audio_url ?? '',
    };
  });
}

const pad3 = (n: number) => String(n).padStart(3, '0');

/** The per-word recitation file, named by surah, ayah and the word's ordinal among real words. */
export function wordByWordAudioUrl(verseKey: string, ordinal: number): string {
  const [surah, ayah] = verseKey.split(':').map(Number);
  return 'https://audio.qurancdn.com/wbw/' + pad3(surah) + '_' + pad3(ayah) + '_' + pad3(ordinal) + '.mp3';
}

/** One word of a verse as our layout lays it out: its location and its text in our script. */
export interface LaidOutWord {
  /** "surah:ayah:word", 1-based word index in mushaf order. */
  loc: string;
  text: string;
}

export function verseKeyOf(loc: string): string {
  return loc.split(':').slice(0, 2).join(':');
}

const wordIndexOf = (loc: string) => +loc.split(':')[2];

/** Our laid-out words that carry Arabic letters, in order (the verse-number glyph drops out). */
function realLaidOutWords(verseWords: LaidOutWord[]): LaidOutWord[] {
  return verseWords.filter((w) => isArabicWord(stripArabic(w.text)));
}

/**
 * The Quran.com word for one of our laid-out words: same bare letters, nearest
 * position when the word repeats in the verse; failing a text match (an unusual
 * glyph), the same ordinal among real words. Null for a mark or the verse number.
 */
export function matchQuranWord(loc: string, verseWords: LaidOutWord[], quranWords: QuranWord[]): QuranWord | null {
  const words = quranWords.filter((w) => w.isWord);
  if (!words.length) return null;
  const mine = stripArabic(verseWords.find((w) => w.loc === loc)?.text);
  if (!isArabicWord(mine)) return null;
  const wantedIndex = wordIndexOf(loc) - 1;
  let best: QuranWord | null = null;
  let bestDistance = Infinity;
  words.forEach((word, i) => {
    if (word.text !== mine) return;
    const distance = Math.abs(i - wantedIndex);
    if (distance < bestDistance) { bestDistance = distance; best = word; }
  });
  if (best) return best;
  const real = realLaidOutWords(verseWords);
  const ordinal = real.length ? real.findIndex((w) => w.loc === loc) : wantedIndex;
  return words[Math.max(0, Math.min(words.length - 1, ordinal))] ?? null;
}

/**
 * The reverse map, for recitation timings: Quran.com's position → our loc.
 * Null for a non-word position (nothing to light up); the naive "same number"
 * loc when the Quran.com words are unavailable, so highlighting degrades
 * rather than stopping.
 */
export function locForTimingPosition(position: number, verseWords: LaidOutWord[], quranWords: QuranWord[]): string | null {
  const quranWord = quranWords.find((w) => w.position === position);
  if (!quranWord || !verseWords.length) {
    return verseWords.length ? verseKeyOf(verseWords[0].loc) + ':' + position : null;
  }
  if (!quranWord.isWord) return null;
  const realIndex = quranWords.filter((w) => w.isWord).indexOf(quranWord);
  let best: string | null = null;
  let bestDistance = Infinity;
  verseWords.forEach((word, i) => {
    if (stripArabic(word.text) !== quranWord.text) return;
    const distance = Math.abs(i - realIndex);
    if (distance < bestDistance) { bestDistance = distance; best = word.loc; }
  });
  if (best) return best;
  return realLaidOutWords(verseWords)[realIndex]?.loc ?? null;
}

type LoadJson = <T>(url: string) => Promise<T>;

/** Quran.com's word list per verse, fetched once; a failed fetch resolves empty and is retried next time. */
export class QuranWordsCache {
  private readonly pending = new Map<string, Promise<QuranWord[]>>();

  constructor(private readonly load: LoadJson = fetchQuranJson) {}

  words(verseKey: string): Promise<QuranWord[]> {
    let promise = this.pending.get(verseKey);
    if (!promise) {
      promise = this.load<QuranComWordsPayload>(quranWordsUrl(verseKey))
        .then(parseQuranWords)
        .catch(() => { this.pending.delete(verseKey); return []; });
      this.pending.set(verseKey, promise);
    }
    return promise;
  }
}

/** The audio file for one of our laid-out words, or '' when it is not a recited word. */
export async function wordAudioUrl(loc: string, verseWords: LaidOutWord[], cache: QuranWordsCache): Promise<string> {
  const match = matchQuranWord(loc, verseWords, await cache.words(verseKeyOf(loc)));
  return match && match.ordinal ? wordByWordAudioUrl(verseKeyOf(loc), match.ordinal) : '';
}
